import * as THREE from 'three';
import '../ui/fortress.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { DungeonEnemy } from '../entities/dungeonEnemy.js';
import { HUD } from '../ui/hud.js';
import { getStat, trainStat, addCrumbs, setFlag, getFlag, spendCrumbs, getCrumbs } from '../core/save.js';
import { addIngredient } from '../core/ingredients.js';
import { flagObjective } from '../core/quests.js';

// The Infinite Toaster — a roguelike dungeon that keeps generating rooms until
// Bread Boi falls. Combat reuses the tap-to-strike mechanics from the Fortress
// Crypt, but enemies scale with floor depth and every 5th floor is a boss.

export function createInfiniteToasterScene(){
  return () => new InfiniteToasterScene();
}

const STONE   = 0x6E6A62;
const STONE_D = 0x4E4A44;
const MORTAR  = 0x36332E;
const WOOD    = 0x5A3A22;
const GOLD    = 0xE8C24B;

const ROOM_TYPES = {
  combat:  { label: 'Crumb Corridor', hint: 'defeat the toast-monsters to advance' },
  boss:    { label: 'Boss Chamber',   hint: 'a burnt behemoth blocks the way' },
  treasure:{ label: 'Treasure Vault', hint: 'open the chest for loot' },
  shop:    { label: 'Toaster Shop',   hint: 'spend crumbs for supplies' },
  rest:    { label: 'Rest Stop',      hint: 'take a breather and heal' }
};

class InfiniteToasterScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.vignette = null;
    this.combatToastT = 0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x120C10);
    this.scene.fog = new THREE.Fog(0x120C10, 8, 28);

    this.camera = new THREE.PerspectiveCamera(52, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 12;
    this.camTarget = new THREE.Vector3(0, 1.6, 2);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoomShell();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.enemies = [];
    this.props = [];
    this.floor = 0;
    this.room = null;
    this.roomCleared = false;
    this.runActive = false;

    this.playerMaxHp = 5 + Math.floor(getStat('str') / 2);
    this.playerHp = this.playerMaxHp;
    this.swingCooldown = 0;
    this.invulnT = 0;

    this.hotspots = [];
    this.nearSpot = null;
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0x3A303A, 0.35));
    const moon = new THREE.DirectionalLight(0x8A6AA8, 0.25);
    moon.position.set(4, 14, 8); moon.castShadow = true;
    moon.shadow.mapSize.set(2048, 2048);
    const s = 16;
    moon.shadow.camera.left = -s; moon.shadow.camera.right = s;
    moon.shadow.camera.top = s; moon.shadow.camera.bottom = -s;
    this.scene.add(moon);
    // warm toaster glow from the back wall
    this.toasterGlow = new THREE.PointLight(0xFF8A3C, 0.4, 12, 1.8);
    this.toasterGlow.position.set(0, 3, -6); this.scene.add(this.toasterGlow);
  }

  _buildRoomShell(){
    const W = 5.5, Z0 = -7, Z1 = 7;
    this.roomBounds = { halfW: W, z0: Z0, z1: Z1 };
    const len = Z1 - Z0, midZ = (Z0 + Z1) / 2;

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(W * 2, len), new THREE.MeshLambertMaterial({ color: STONE }));
    floor.rotation.x = -Math.PI/2; floor.position.set(0, 0, midZ); floor.receiveShadow = true; this.scene.add(floor);

    const wallMat = new THREE.MeshLambertMaterial({ color: STONE_D });
    const wallGeo = new THREE.PlaneGeometry(len, 7);
    const wallL = new THREE.Mesh(wallGeo, wallMat);
    wallL.rotation.y = Math.PI/2; wallL.position.set(-W, 3.5, midZ); wallL.receiveShadow = true; this.scene.add(wallL);
    const wallR = new THREE.Mesh(wallGeo, wallMat);
    wallR.rotation.y = -Math.PI/2; wallR.position.set(W, 3.5, midZ); wallR.receiveShadow = true; this.scene.add(wallR);

    // back wall with the "next room" arch
    box(W * 2, 7, 0.4, STONE_D, 0, 3.5, Z0, this.scene);
    const archW = 2.4;
    box((W * 2 - archW) / 2, 7, 0.5, STONE_D, -(archW/2 + (W * 2 - archW)/4), 3.5, Z0, this.scene);
    box((W * 2 - archW) / 2, 7, 0.5, STONE_D,  (archW/2 + (W * 2 - archW)/4), 3.5, Z0, this.scene);
    box(archW + 0.2, 2.2, 0.5, STONE, 0, 5.8, Z0, this.scene);

    // entrance arch
    box((W * 2 - archW) / 2, 7, 0.5, STONE_D, -(archW/2 + (W * 2 - archW)/4), 3.5, Z1, this.scene);
    box((W * 2 - archW) / 2, 7, 0.5, STONE_D,  (archW/2 + (W * 2 - archW)/4), 3.5, Z1, this.scene);
    box(archW + 0.2, 2.2, 0.5, STONE, 0, 5.8, Z1, this.scene);

    // ceiling
    const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W * 2, len), new THREE.MeshLambertMaterial({ color: 0x181014 }));
    ceil.rotation.x = Math.PI/2; ceil.position.set(0, 7, midZ); this.scene.add(ceil);

    // floor label that changes per room
    this.roomLabel = textSprite('The Infinite Toaster', 28, '#EFE6D0', 6, 1.0);
    this.roomLabel.position.set(0, 4.8, -6.8); this.scene.add(this.roomLabel);
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.32, 0.6];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.vignette = document.createElement('div');
    this.vignette.className = 'fortress-vignette';
    document.body.appendChild(this.vignette);
    this._positionCamera();
    this._buildCombatHud();
    this._startRun();
  }

  exit(){
    this._clearPanels();
    if (this.vignette){ this.vignette.remove(); this.vignette = null; }
    if (this.hudEl){ this.hudEl.remove(); this.hudEl = null; }
    this.input.onTap = null;
    HUD.showEnter(null); HUD.say('');
  }

  _positionCamera(){
    this.azimuth = this.input.azimuthGoal ?? 0;
    this.polar   = this.input.polarGoal ?? 0.5;
    const b = this.roomBounds;
    this.camTarget.set(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      1.8 + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  _clampCamera(){
    const b = this.roomBounds;
    const pad = 1.2;
    this.camera.position.x = Math.max(-b.halfW + pad, Math.min(b.halfW - pad, this.camera.position.x));
    this.camera.position.z = Math.max(b.z0 + pad, Math.min(b.z1 - pad, this.camera.position.z));
    this.camera.position.y = Math.max(2, Math.min(9, this.camera.position.y));
  }

  _buildCombatHud(){
    this.hudEl = document.createElement('div');
    this.hudEl.className = 'dungeon-hud';
    this.hudEl.innerHTML = `
      <div class="dungeon-hp"><span id="toaster-hp-bar" class="ok" style="width:100%"></span></div>
      <div class="toaster-floor">Floor <span id="toaster-floor">1</span></div>
    `;
    document.body.appendChild(this.hudEl);
    this.hpBar = this.hudEl.querySelector('#toaster-hp-bar');
    this.floorEl = this.hudEl.querySelector('#toaster-floor');
  }

  _updateHpBar(){
    if (!this.hpBar) return;
    const pct = Math.max(0, (this.playerHp / this.playerMaxHp) * 100);
    this.hpBar.style.width = pct + '%';
    this.hpBar.className = pct > 60 ? 'ok' : pct > 30 ? 'warn' : 'crit';
  }

  _combatToast(text){
    this.combatToastT = 1.2;
    if (!this.toastEl){
      this.toastEl = document.createElement('div');
      this.toastEl.className = 'dungeon-toast';
      document.body.appendChild(this.toastEl);
    }
    this.toastEl.textContent = text;
    this.toastEl.style.opacity = '1';
  }

  // ---- run flow ----
  _startRun(){
    this.floor = 0;
    this.playerMaxHp = 5 + Math.floor(getStat('str') / 2);
    this.playerHp = this.playerMaxHp;
    this._updateHpBar();
    this.runActive = true;
    HUD.setSubtitle('the infinite toaster');
    HUD.say('The Toaster rumbles. Room after room of burnt dough awaits.');
    this._nextRoom();
  }

  _nextRoom(){
    this.floor++;
    if (this.floor === 5) flagObjective('toaster-run', 'reachedToasterFloor5');
    if (this.floorEl) this.floorEl.textContent = this.floor;
    this._clearRoom();

    const isBoss = this.floor % 5 === 0;
    let room;
    if (isBoss){
      room = { type: 'boss' };
    } else {
      const roll = Math.random();
      if (roll < 0.62){
        const type = Math.random() < 0.55 ? 'skeleton' : 'golem';
        const count = Math.min(3, 1 + Math.floor(this.floor / 8) + (Math.random() < 0.35 ? 1 : 0));
        room = { type: 'combat', enemyType: type, count };
      } else if (roll < 0.74){
        room = { type: 'treasure' };
      } else if (roll < 0.86){
        room = { type: 'shop' };
      } else {
        room = { type: 'rest' };
      }
    }
    this.room = room;
    this.roomCleared = room.type !== 'combat' && room.type !== 'boss';
    this._spawnRoom();
  }

  _clearRoom(){
    this.enemies.forEach(e => this.scene.remove(e.root));
    this.enemies = [];
    this.props.forEach(p => this.scene.remove(p));
    this.props = [];
    this.hotspots = [];
    this.nearSpot = null;
    HUD.showEnter(null);
  }

  _spawnRoom(){
    const info = ROOM_TYPES[this.room.type];
    this.roomLabel.material.map.image.getContext('2d').clearRect(0,0,256,128);
    // rebuild sprite text
    const sprite = textSprite(`Floor ${this.floor} — ${info.label}`, 24, '#EFE6D0', 7, 1.0);
    this.roomLabel.material.map = sprite.material.map;
    this.roomLabel.material.map.needsUpdate = true;

    HUD.setHint(info.hint);
    HUD.hideHintSoon(6000);

    if (this.room.type === 'combat' || this.room.type === 'boss'){
      this._spawnEnemies();
      return;
    }

    if (this.room.type === 'treasure'){
      const chest = this._makeChest();
      chest.position.set(0, 0, -2); this.scene.add(chest); this.props.push(chest);
      this.hotspots.push({ pos: new THREE.Vector3(0, 0, -1), r: 1.8, label: '📦 open chest', action: () => this._openChest() });
    } else if (this.room.type === 'shop'){
      const keeper = this._makeShopkeeper();
      keeper.position.set(0, 0, -2); this.scene.add(keeper); this.props.push(keeper);
      this.hotspots.push({ pos: new THREE.Vector3(0, 0, -1), r: 2.0, label: '🛒 shop', action: () => this._openShop() });
    } else if (this.room.type === 'rest'){
      const fire = this._makeCampfire();
      fire.position.set(0, 0, -2); this.scene.add(fire); this.props.push(fire);
      this.hotspots.push({ pos: new THREE.Vector3(0, 0, -1), r: 2.0, label: '🔥 rest (+2 HP)', action: () => this._rest() });
    }

    // peaceful rooms still let you advance or abandon
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, -6.2), r: 1.8, label: '➡ next room', action: () => this._nextRoom() });
    this.hotspots.push({ pos: new THREE.Vector3(0, 0, 6.2), r: 1.8, label: '🚪 abandon run', action: () => this._abandon() });
  }

  _spawnEnemies(){
    const count = this.room.type === 'boss' ? 1 : this.room.count;
    const type = this.room.type === 'boss' ? 'burnt' : this.room.enemyType;
    const spacing = 1.6;
    const startX = -((count - 1) * spacing) / 2;
    for (let i = 0; i < count; i++){
      const e = new DungeonEnemy(type);
      e.root.position.set(startX + i * spacing, 0, -2 - (this.room.type === 'boss' ? 1.5 : 0));
      this._scaleEnemy(e);
      this.scene.add(e.root);
      this.enemies.push(e);
    }
    this.roomCleared = false;
  }

  _scaleEnemy(e){
    const s = 1 + (this.floor - 1) * 0.22;
    e.maxHp = Math.max(1, Math.floor(e.maxHp * s));
    e.hp = e.maxHp;
    e.damage = Math.max(1, Math.floor(e.damage * (1 + (this.floor - 1) * 0.12)));
  }

  _checkRoomClear(){
    if (this.roomCleared) return;
    if (this.enemies.every(e => e.dead)){
      this.roomCleared = true;
      addCrumbs(2 + this.floor); HUD.refreshCrumbs();
      this._combatToast(`room cleared! +${2 + this.floor} crumbs`);
      this.hotspots.push({ pos: new THREE.Vector3(0, 0, -6.2), r: 1.8, label: '➡ next room', action: () => this._nextRoom() });
      this.hotspots.push({ pos: new THREE.Vector3(0, 0, 6.2), r: 1.8, label: '🚪 leave run', action: () => this._finishRun(false) });
    }
  }

  // ---- interactions ----
  _tap(ray){
    if (this.state === 'defeat') return;
    if (this.swingCooldown <= 0 && this._checkEnemyClick(ray)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-5, Math.min(5, gp.x));
    gp.z = Math.max(-6.5, Math.min(6.5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _checkEnemyClick(ray){
    const targets = this.enemies.filter(e => !e.dead).map(e => e.hitTarget);
    if (!targets.length) return false;
    const hits = ray.intersectObjects(targets, false);
    if (hits.length){
      const enemy = hits[0].object.userData.enemy;
      if (enemy) this._swingAt(enemy);
      return true;
    }
    return false;
  }

  _swingAt(enemy){
    this.bread.stop();
    this.bread.startPose('strike', 0.35);
    this.bread.drawFace('effort');
    this.swingCooldown = 0.45;
    const dmg = 1 + Math.floor(getStat('str') * 0.5);
    const killed = enemy.takeDamage(dmg);
    this._combatToast(killed ? 'toasted!' : `hit -${dmg}`);
    if (killed){
      addCrumbs(1 + Math.floor(this.floor / 3));
      HUD.refreshCrumbs();
      if (Math.random() < 0.2) addIngredient('bone-meal', 1);
      this._checkRoomClear();
    }
  }

  _openChest(){
    // remove hotspot so it can't be reopened
    this.hotspots = this.hotspots.filter(h => !h.label.includes('chest'));
    this.nearSpot = null; HUD.showEnter(null);
    const reward = 10 + this.floor * 3;
    addCrumbs(reward); HUD.refreshCrumbs();
    if (Math.random() < 0.5){
      const ing = ['berry', 'mushroom', 'herb', 'honey'][(Math.random()*4)|0];
      addIngredient(ing, 1);
      HUD.say(`Treasure! +${reward} crumbs and ${ing}.`);
    } else {
      HUD.say(`Treasure! +${reward} crumbs.`);
    }
    this.bread.drawFace('yum');
  }

  _openShop(){
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">🛒</div>
        <h2>Toaster Shop</h2>
        <p>Spend crumbs to keep the run going.</p>
        <button class="cafe-btn buy-heal" ${getCrumbs() < 25 ? 'disabled' : ''}>🍞 heal +3 HP — 25 crumbs</button>
        <button class="cafe-btn buy-bone">🦴 bone meal — 15 crumbs</button>
        <button class="cafe-btn ghost close">leave</button>
      </div>
    `);
    p.querySelector('.buy-heal').onclick = () => {
      if (!spendCrumbs(25)) return;
      this.playerHp = Math.min(this.playerMaxHp, this.playerHp + 3);
      this._updateHpBar();
      HUD.refreshCrumbs();
      this._openShop();
    };
    p.querySelector('.buy-bone').onclick = () => {
      if (!spendCrumbs(15)) return;
      addIngredient('bone-meal', 1);
      HUD.refreshCrumbs();
      this._openShop();
    };
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _rest(){
    this.hotspots = this.hotspots.filter(h => !h.label.includes('rest'));
    this.nearSpot = null; HUD.showEnter(null);
    this.playerHp = Math.min(this.playerMaxHp, this.playerHp + 2);
    this._updateHpBar();
    HUD.say('You rest by the fire. +2 HP.');
    this.bread.drawFace('grin');
  }

  _abandon(){ this._finishRun(false); }

  _finishRun(defeated){
    this.runActive = false;
    this.state = 'defeat';
    const reward = this.floor * 8;
    addCrumbs(reward); HUD.refreshCrumbs();
    const best = getFlag('toasterBestFloor') ? parseInt(localStorage.getItem('toasttown.toasterBest') || '0') : 0;
    const newBest = Math.max(best, this.floor);
    setFlag('toasterBestFloor', true);
    try { localStorage.setItem('toasttown.toasterBest', String(newBest)); } catch(e){}

    let statText = '';
    if (!defeated && this.floor >= 10){
      const stats = ['str', 'agi', 'int', 'zen'];
      const pick = stats[(Math.random() * stats.length) | 0];
      const gain = trainStat(pick, 0.5);
      if (gain > 0){ statText = ` · +${gain} ${pick.toUpperCase()}`; HUD.refreshStats(); }
    }

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${defeated ? '💀' : '🏁'}</div>
        <h2>${defeated ? 'Run Toasted' : 'Run Complete'}</h2>
        <p class="cafe-payout">Floor reached: <b>${this.floor}</b> · Best: <b>${newBest}</b></p>
        <p class="cafe-payout">🍞 +${reward} crumbs${statText}</p>
        <button class="cafe-btn restart">try again</button>
        <button class="cafe-btn ghost close">back to town</button>
      </div>
    `);
    p.querySelector('.restart').onclick = () => { this._clearPanels(); this.state = 'explore'; this._startRun(); };
    p.querySelector('.close').onclick = () => this.manager.goTo('town', { from: 'infiniteToaster' });
  }

  _defeat(){
    this._finishRun(true);
  }

  // ---- prop builders ----
  _makeChest(){
    const g = new THREE.Group();
    box(1.4, 0.7, 0.8, WOOD, 0, 0.35, 0, g);
    box(1.5, 0.1, 0.9, GOLD, 0, 0.35, 0, g);
    box(0.12, 0.7, 0.9, GOLD, -0.6, 0.35, 0, g);
    box(0.12, 0.7, 0.9, GOLD, 0.6, 0.35, 0, g);
    box(0.2, 0.22, 0.08, GOLD, 0, 0.4, 0.45, g);
    return g;
  }

  _makeShopkeeper(){
    const g = new THREE.Group();
    sphere(0.35, 0xD4A76A, 0, 1.2, 0, g, 12);
    box(0.55, 0.5, 0.3, 0x8C6239, 0, 0.7, 0, g);
    box(0.4, 0.08, 0.35, 0xFFFFFF, 0, 1.45, 0, g);
    sphere(0.06, 0x1A1A1A, -0.12, 1.28, 0.28, g, 8);
    sphere(0.06, 0x1A1A1A, 0.12, 1.28, 0.28, g, 8);
    cyl(0.04, 0.04, 1.0, 0x6B4A2F, -0.35, 0.5, 0.1, g, 6).rotation.z = 0.4;
    cyl(0.04, 0.04, 1.0, 0x6B4A2F, 0.35, 0.5, 0.1, g, 6).rotation.z = -0.4;
    return g;
  }

  _makeCampfire(){
    const g = new THREE.Group();
    for (let i = 0; i < 6; i++){
      const a = (i / 6) * Math.PI * 2;
      box(0.12, 0.25, 0.4, 0x5A3A22, Math.cos(a)*0.35, 0.125, Math.sin(a)*0.35, g).rotation.y = a;
    }
    const flame = new THREE.Group(); flame.position.y = 0.35; g.add(flame);
    const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.5, 8), new THREE.MeshBasicMaterial({ color: 0xFF9A3C }));
    c1.position.y = 0.2; flame.add(c1);
    this.campfireLight = new THREE.PointLight(0xFF9A3C, 0.6, 5, 1.5);
    this.campfireLight.position.set(0, 0.45, 0); g.add(this.campfireLight);
    return g;
  }

  // ---- panels ----
  _panel(html){
    const el = document.createElement('div');
    el.className = 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  // ---- loop ----
  update(dt){
    const t = performance.now() * 0.001;

    // ambient toaster glow pulse
    if (this.toasterGlow) this.toasterGlow.intensity = 0.35 + 0.15 * Math.sin(t * 2);
    if (this.campfireLight) this.campfireLight.intensity = 0.5 + 0.15 * Math.sin(t * 8);

    if (this.state === 'defeat') return;
    if (this.swingCooldown > 0) this.swingCooldown -= dt;
    this.invulnT = Math.max(0, this.invulnT - dt);

    const playerPos = this.bread.root.position;

    // enemies
    this.enemies.forEach(e => {
      if (e.dead) return;
      const d = e.root.position.distanceTo(playerPos);
      if (e.state === 'idle' && d < 5.5) e.startAttack();
      e.update(dt, playerPos);
      if (e.state === 'attacking' && !e.dealtDamage && d < 1.6){
        e.dealtDamage = true;
        const dodgeChance = Math.min(0.4, getStat('agi') * 0.07);
        if (Math.random() < dodgeChance){
          this._combatToast('dodged!');
        } else if (this.invulnT <= 0){
          this.playerHp -= e.damage;
          this.invulnT = 0.8;
          this._updateHpBar();
          this._combatToast('ouch! -' + e.damage + ' HP');
          this.bread.drawFace('o');
          if (this.playerHp <= 0) this._defeat();
        }
      }
      if (e.state !== 'attacking') e.dealtDamage = false;
    });

    if (this.combatToastT > 0){
      this.combatToastT -= dt;
      if (this.combatToastT <= 0 && this.toastEl) this.toastEl.style.opacity = '0';
    }

    this.bread.update(dt);

    // proximity hotspots
    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(playerPos);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    const b = this.roomBounds;
    this.camTarget.lerp(new THREE.Vector3(playerPos.x * 0.5, 1.6, playerPos.z * 0.5 + 1), 0.06);
    this.camTarget.z = Math.max(b.z0 + 2, Math.min(b.z1 - 2, this.camTarget.z));
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this._clampCamera();
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
